import { useEffect, useState } from 'react';

import type { BulkOrchestratorState } from '../hooks/useBulkEpisodeOrchestrator';

interface BulkProgressSummaryProps {
  state: BulkOrchestratorState;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

export function BulkProgressSummary({ state }: BulkProgressSummaryProps) {
  const total = state.episodes.length;
  const completed = state.succeeded + state.failed;
  const inProgress = state.episodes.filter((ep) =>
    Object.values(ep.steps).some((s) => s === 'in_progress')
  ).length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Tick elapsed time every second while running
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (state.status !== 'running') return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [state.status]);

  const elapsed = state.startedAt ? now - state.startedAt.getTime() : 0;
  const estimated =
    state.startedAt && completed > 0
      ? Math.round((elapsed / completed) * (total - completed))
      : null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Overall Progress: {completed}/{total} completed ({percent}%)
        </h3>
        <div className="flex gap-4 text-sm text-gray-500">
          {state.startedAt && <span>Elapsed: {formatDuration(elapsed)}</span>}
          {estimated !== null && state.status === 'running' && (
            <span>Est. remaining: ~{formatDuration(estimated)}</span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-3 h-3 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full rounded-full bg-indigo-600 transition-all duration-300"
          style={{ width: `${percent}%` }}
        />
      </div>

      {/* Stats */}
      <div className="flex gap-6 text-sm">
        <span className="text-green-600">
          Succeeded: <strong>{state.succeeded}</strong>
        </span>
        <span className="text-red-600">
          Failed: <strong>{state.failed}</strong>
        </span>
        <span className="text-blue-600">
          In Progress: <strong>{inProgress}</strong>
        </span>
        <span className="text-gray-400">
          Pending: <strong>{total - completed - inProgress}</strong>
        </span>
      </div>
    </div>
  );
}
